import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, FormTextInput, Loader } from '@liveconnect/components'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import FormActions from '../../../components/FormActions'
import { CometchatLicense } from '../../../core/licenses/types'
import useLicenses from '../../../core/licenses/useLicenses'
import useNotifications from '../../../utils/notifications/useNotifications'
import { buildValidationSchema } from './validations'

const CometchatLicenseForm = () => {
  const { t } = useTranslation()
  const notify = useNotifications()
  const { cometchat, fetchCometchat, updateCometchat } = useLicenses()
  const methods = useForm<CometchatLicense>({
    mode: 'onChange',
    resolver: yupResolver(buildValidationSchema(t)),
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid, isSubmitting },
  } = methods

  const handleCancel = () => {
    cometchat && reset(cometchat)
  }

  const onSubmit = async (data: CometchatLicense) => {
    try {
      await updateCometchat(data)
      notify.success(t('license.cometchat.form.success'))
    } catch (e) {
      notify.error(t('license.cometchat.form.error'))
    }
  }

  useEffect(() => {
    cometchat && reset(cometchat)
  }, [cometchat])

  useEffect(() => {
    fetchCometchat()
  }, [])

  return (
    <div>
      <div className="sticky-container">
        <FormProvider methods={methods}>
          <div className="row">
            <div className="col-6">
              <FormTextInput
                control={control}
                name="region"
                label={t('license.cometchat.form.region.label')}
                placeholder={t('license.cometchat.form.region.placeholder')}
                type="text"
                required={true}
              />
            </div>
            <div className="col-6">
              <FormTextInput
                control={control}
                name="appId"
                label={t('license.cometchat.form.app.label')}
                placeholder={t('license.cometchat.form.app.placeholder')}
                type="text"
                required={true}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <FormTextInput
                control={control}
                name="apiKey"
                label={t('license.cometchat.form.api.label')}
                placeholder={t('license.cometchat.form.api.placeholder')}
                type="text"
                required={true}
              />
            </div>
            <div className="col-6">
              <FormTextInput
                control={control}
                name="defaultRole"
                label={t('license.cometchat.form.role.label')}
                placeholder={t('license.cometchat.form.role.placeholder')}
                type="text"
                required={true}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <FormTextInput
                control={control}
                name="defaultAvatar"
                label={t('license.cometchat.form.avatar.label')}
                placeholder={t('license.cometchat.form.avatar.placeholder')}
                type="text"
                required={true}
              />
            </div>
          </div>
        </FormProvider>
      </div>
      <FormActions>
        <div>
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-outline-primary mr-2"
          >
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            disabled={isSubmitting || !isValid || !isDirty}
            onClick={handleSubmit(onSubmit)}
          >
            {isSubmitting ? <Loader /> : t(`common.save`)}
          </button>
        </div>
      </FormActions>
    </div>
  )
}

export default CometchatLicenseForm
